import { useState } from "react";
import { useTranslation } from "react-i18next";
import { parentRegister } from "@/api";
import Modal from "../ui/Modal";

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSwitchToLogin: () => void;
}

export default function RegisterModal({ isOpen, onClose, onSuccess, onSwitchToLogin }: RegisterModalProps) {
  const { t, i18n } = useTranslation("common");
  const isRTL = i18n.language === "ar";

  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !phoneNumber.trim() || !email.trim() || !password.trim()) {
      setError(t("fillAllFields"));
      return;
    }

    if (!email.includes("@")) {
      setError(t("invalidEmailAddress"));
      return;
    }

    if (password.length < 6) {
      setError(t("passwordMinError"));
      return;
    }

    setError("");
    setLoading(true);

    try {
      const data = await parentRegister({
        name,
        phoneNumber,
        email,
        password,
      });
      localStorage.setItem("parent_token", data.token);
      localStorage.setItem("parent_name", data.parent.name);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error(err);
      if (err.message === "phone_already_registered") {
        setError(t("phoneAlreadyRegistered"));
      } else if (err.message === "email_already_registered") {
        setError(t("emailAlreadyRegistered"));
      } else {
        setError(t("registrationFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("parentRegisterTitle")} maxWidth="md">
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-500 rounded-xl text-xs flex items-center gap-2">
          <i className="ri-error-warning-line text-sm" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
            {t("fullName")}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("fullNamePlaceholder")}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
            dir="auto"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
            {t("phoneNumber")}
          </label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
            placeholder="05xxxxxxxx"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
            {t("emailAddress")}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@domain.com"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
            {t("password")}
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
              style={{ [isRTL ? "left" : "right"]: "10px" }}
              tabIndex={-1}
              aria-label={showPassword ? t("hidePassword") : t("showPassword")}
              title={showPassword ? t("hidePassword") : t("showPassword")}
            >
              <i className={showPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"} />
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 bg-primary-400 hover:bg-primary-500 disabled:bg-gray-300 text-white rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
        >
          {loading ? (
            <span>{t("creatingAccount")}</span>
          ) : (
            <>
              <i className="ri-user-add-line" />
              <span>{t("createAccount")}</span>
            </>
          )}
        </button>
      </form>

      <div className="mt-5 text-center text-sm text-gray-500">
        {t("alreadyHaveAccount")}{" "}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-primary-400 hover:text-primary-500 font-semibold"
        >
          {t("login")}
        </button>
      </div>
    </Modal>
  );
}
